import { createTheme, type ThemeOptions } from '@mui/material/styles';

const LibreFranklin = "'Libre Franklin', sans-serif";

const typography = {
  fontFamily: LibreFranklin,
  h1: {
    fontFamily: LibreFranklin,
    fontWeight: 500,
    fontSize: '3rem',
  },
  h2: {
    fontFamily: LibreFranklin,
    fontWeight: 500,
    fontSize: '2.25rem',
  },
  h3: {
    fontFamily: LibreFranklin,
    fontWeight: 500,
    fontSize: '1.8rem',
  },
  h4: {
    fontFamily: LibreFranklin,
    fontWeight: 400,
    fontSize: '1.5rem',
  },
  h5: {
    fontFamily: LibreFranklin,
    fontWeight: 400,
    fontSize: '1.25rem',
  },
  h6: {
    fontFamily: LibreFranklin,
    fontWeight: 400,
    fontSize: '1.1rem',
  },
  subtitle1: {
    fontFamily: LibreFranklin,
    fontWeight: 500,
    fontSize: '1rem',
  },
  subtitle2: {
    fontFamily: LibreFranklin,
    fontWeight: 400,
    fontSize: '0.95rem',
  },
  body1: {
    fontFamily: LibreFranklin,
    fontWeight: 400,
    fontSize: '1rem',
  },
  body2: {
    fontFamily: LibreFranklin,
    fontWeight: 400,
    fontSize: '0.9rem',
  },
  button: {
    fontFamily: LibreFranklin,
    fontWeight: 500,
    fontSize: '0.95rem',
    textTransform: 'none' as const,
  },
  caption: {
    fontFamily: LibreFranklin,
    fontWeight: 400,
    fontSize: '0.85rem',
  },
  overline: {
    fontFamily: LibreFranklin,
    fontWeight: 400,
    fontSize: '0.8rem',
  },
};

const lightThemeOptions: ThemeOptions = {
  palette: {
    mode: 'light',
    primary: {
      main: '#98773b',
    },
    secondary: {
      main: '#f24e1e',
    },
    background: {
      default: '#0a1c41',
      paper: '#ffffff',
    },
    text: {
      primary: '#000000',
      secondary: 'rgba(0, 0, 0, 0.7)',
    },
    success: {
      main: '#3DF06A',
      contrastText: '#173F2C',
    },
    error: {
      main: '#FF4E4E',
      contrastText: '#3D1F1F',
    },
    info: {
      main: '#98773b',
      contrastText: '#2C1F3D',
    },
    divider: '#DDDDDD',
  },
  typography,
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontFamily: LibreFranklin,
          fontWeight: 500,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontFamily: LibreFranklin,
          fontWeight: 400,
          fontSize: '1rem',
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          color: '#000',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 2px 16px #0002',
          border: '1px solid #DDDDDD',
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          overflow: 'hidden',
          border: '1px solid #DDDDDD',
        },
      },
    },
  },
};

const darkThemeOptions: ThemeOptions = {
  palette: {
    mode: 'dark',
    primary: {
      main: '#98773b',
    },
    secondary: {
      main: '#f24e1e',
    },
    background: {
      default: '#000000',
      paper: '#222222',
    },
    text: {
      primary: '#ffffff',
      secondary: 'rgba(255, 255, 255, 0.7)',
    },
    success: {
      main: '#3DF06A',
      contrastText: '#173F2C',
    },
    error: {
      main: '#FF4E4E',
      contrastText: '#3D1F1F',
    },
    info: {
      main: '#98773b',
      contrastText: '#2C1F3D',
    },
    divider: '#333',
  },
  typography,
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontFamily: LibreFranklin,
          fontWeight: 500,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontFamily: LibreFranklin,
          fontWeight: 400,
          fontSize: '1rem',
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          color: '#fff',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 2px 16px #0002',
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          overflow: 'hidden',
        },
      },
    },
  },
};

export const getTheme = (mode: 'light' | 'dark') => {
  return createTheme(mode === 'light' ? lightThemeOptions : darkThemeOptions);
} 
